import React from 'react';
import { Modal, Typography, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import { useOrganizationTableColumns } from '~/app/hooks/organization/useOrganizationTableColumns';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type OrganizationTableProps = {
  organizations: FindOrganizationDto[];
  onEdit: (org: FindOrganizationDto) => void;
  onDelete: (orgId: string) => void;
  onStatusToggle: (orgId: string) => void;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
};

export function BackOfficeTable({
  organizations,
  onEdit,
  onDelete,
  onStatusToggle,
  total,
  page,
  pageSize,
  onPageChange,
}: OrganizationTableProps) {
  const { t } = useTranslation('organization');

  const dataSource = organizations.map((org) => ({
    id: org.id,
    name: org.name,
    organizationType: org.organizationType,
    active: org.active,
    parentId: org.parentId,
    mainLanguage: org.mainLanguage,
    createdAt: org.createdAt,
  }));

  const [modalInfo, setModalInfo] = React.useState<{
    orgId: string;
    action: 'delete' | 'status';
  } | null>(null);

  const columns = useOrganizationTableColumns({
    setModalInfo,
    onEdit,
    t,
  });

  const getModalActionKey = (action: 'delete' | 'status') =>
    action === 'delete' ? 'delete' : 'update';

  return (
    <Flex
      style={{
        background: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        boxShadow: 'none',
      }}
      vertical
    >
      <EvimoTable
        columns={columns}
        dataSource={dataSource}
        rowKey={(record) => record.id}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: onPageChange,
        }}
        onRow={(record) => ({
          onClick: () => {
            window.location.href = `/admin/back-office/organizations/${record.id}`;
          },
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: t('organization:emptyText') }}
      />
      <Modal
        open={!!modalInfo}
        title={t('organization:modal.title')}
        onCancel={() => setModalInfo(null)}
        onOk={() => {
          if (modalInfo?.action === 'delete') onDelete(modalInfo.orgId);
          else if (modalInfo?.action === 'status')
            onStatusToggle(modalInfo.orgId);
          setModalInfo(null);
        }}
        okText={t('organization:modal.ok')}
        cancelText={t('organization:modal.cancel')}
      >
        <Typography.Text>
          {modalInfo &&
            t('organization:modal.body', {
              action: t(
                `organization:modal.${getModalActionKey(modalInfo.action)}`,
              ),
              name:
                dataSource.find((o) => o.id === modalInfo.orgId)?.name || '',
            })}
        </Typography.Text>
      </Modal>
    </Flex>
  );
}
